import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { DOCUMENT_PUBLISH_OPTIONS } from '../../../constants/courses';
import courseOffering from '../../../proptypes/courses/courseOffering';
import courseOfferingMethod from '../../../proptypes/courses/courseOfferingMethod';
import { formatDuration, formatDateTime, formatTime, getCurrentRegistration } from "../../../utilities/courseUtilities";

import * as courseSelectors from "../../../selectors/courseSelectors";

import CourseOfferingDetails from './CourseOfferingDetails';
import PresenterList from '../presenter/PresenterList';
import CourseDocumentList from './CourseDocumentList';

import courseOfferingFormat from '../../../proptypes/courses/courseOfferingFormat';
import CourseOfferingDescription from './CourseOfferingDescription';
import { COURSE_SERIES } from "../../../constants/courseSeriesLookup";
import { CONFIGURATION } from "../../../constants/config";
import moment from 'moment';
import SvgIcon from '../../icon/SvgIcon';

function filterCourseDocuments(currentOffering, registeredCourses) {
  let courseRegistration = registeredCourses
    ? getCurrentRegistration(registeredCourses, currentOffering.uniqueId)
    : false;

  let filteredCourseDocuments = currentOffering.documents.filter(document =>
    document.documentPublishOption === DOCUMENT_PUBLISH_OPTIONS.IMMEDIATE
    || (document.documentPublishOption === DOCUMENT_PUBLISH_OPTIONS.UPON_REGISTRATION && courseRegistration)
    || (document.documentPublishOption === DOCUMENT_PUBLISH_OPTIONS.UPON_COMPLETION && courseRegistration && courseRegistration.attended)
  );

  return filteredCourseDocuments;
}

const CourseOfferingDetailBody = ({
  currentOffering,
  registeredCourses,
  previouslyLoadedCourse,
}) => {
  let isOnDemand = currentOffering.method === courseOfferingMethod.ON_DEMAND;
  let hasOutline =
    currentOffering.lmsOutline && currentOffering.lmsOutline.length > 1;
  const pendingCDIApprovalInd = courseOffering.course.pendingCDIApprovalInd;

  let showCourseDetails =
    !previouslyLoadedCourse ||
    !isOnDemand ||
    (isOnDemand && (pendingCDIApprovalInd || hasOutline));

  const hasPresenters = currentOffering.presenters && currentOffering.presenters.length > 0;
  const hasDocuments = currentOffering.documents && currentOffering.documents.length > 0;

  let isLive = currentOffering.method === courseOfferingMethod.LIVE;
  let onDemand = currentOffering.method === courseOfferingMethod.ON_DEMAND;
  let hasLocation = currentOffering.location ? true : false;
  let isNonCeCourse =
  currentOffering.course.seriesId === COURSE_SERIES.COMPLIANCE_CONNECTIONS ||
    (onDemand && currentOffering.course.name === CONFIGURATION.INTRO_COURSE_NAME);

  if (showCourseDetails || hasPresenters || hasDocuments) {    
    return (
      <div role="main" className="detail-body-wrapper">
        <div className="row detail-body">
          {showCourseDetails && (
            <div className="col-md-12 col-lg-5 col-xl-4 course-details">
              <CourseOfferingDetails />
            </div>
          )}
          <div className="col-md-12 col-lg-7 col-xl-8 course-extras">
            {
              // New code moved few course header details to body              
              <div role="main" className="detail-body-wrapper">
                <div className='row course-info'>
                  {isLive && currentOffering.format == courseOfferingFormat.WEBINAR &&
                    <div className='col-12 col-lg-auto'>
                      <div className='svg-icon webinar-sm live-course' />
                      <span> Webinar </span>
                    </div>
                  }

                  {isLive && currentOffering.format == courseOfferingFormat.IN_PERSON && hasLocation && currentOffering.location.address &&
                    <div className='col-12 col-lg-auto'>
                      <div className='svg-icon pin-sm live-course' />
                      <span> {currentOffering.location.address.city} </span>
                    </div>
                  }

                  {isLive &&
                    <div className='col-12 col-lg-auto'>
                      <div className='svg-icon cal-sm live-course' />
                      <span> {formatDateTime(currentOffering.date)} </span>
                    </div>
                  }

                  {!isLive &&
                    <div className='col-12 col-lg-auto'>
                      <div className='svg-icon webinar-sm on-demand-course' />
                      <span> On Demand </span>
                    </div>
                  }

                  {(courseOffering.duration > 0 || courseOffering.date) && (
                    <div className='col-12 col-lg-auto'>
                      {isLive && 
                        <SvgIcon icon="clock-sm" className='live-course' />
                      }
                      {!isLive &&
                        <SvgIcon icon="clock-sm" className='on-demand-course' />
                      }
                      <span className="detail"> {formatDuration(currentOffering.duration)} </span>
                      {isLive && currentOffering.duration > 0 && currentOffering.date && <span>|</span>}
                      {isLive && currentOffering.date && <span>{formatTime(currentOffering.date)} PT</span>}
                    </div>
                  )}

                  <div className='col-12 col-lg-auto'>
                    {isLive &&
                      <SvgIcon icon="credits-number-sm" className='live-course' />
                    }
                    {!isLive &&
                      <SvgIcon icon="credits-number-sm" className='on-demand-course' />
                    }
                    {" "}
                    {(currentOffering.course.ceHours == 0) && (
                      <span className="detail">
                        No Credit
                      </span>
                    )}
                    {(currentOffering.course.ceHours > 0) && (
                      <span className="detail">
                        {currentOffering.course.ceHours} Credits
                      </span>
                    )}
                  </div>

                  {(!isNonCeCourse && currentOffering.course.cdiCourseCode) && (
                    <div className='col-12 col-lg-auto'>
                      {isLive &&
                        <SvgIcon icon="course-number-sm" className='live-course' />
                      }
                      {!isLive &&
                        <SvgIcon icon="course-number-sm" className='on-demand-course' />
                      }
                      <span> Course # {currentOffering.course.cdiCourseCode}</span>
                    </div>
                  )}
                </div>

                <hr />

                {isOnDemand && (
                  <div className=''>
                    <span><b>Published:</b> {currentOffering.publishDate === '' ? null : moment(currentOffering.publishDate).format("MMM DD, YYYY")}</span>
                  </div>
                )}

                <br />
                <CourseOfferingDescription description={currentOffering.description} />
              </div>
            }            

            {hasPresenters && (
              <PresenterList presenters={currentOffering.presenters} />
            )}            

            {hasDocuments && (
              <CourseDocumentList
                documents={filterCourseDocuments(currentOffering, registeredCourses)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
  else
    return null;
};

CourseOfferingDetailBody.propTypes = {
  currentOffering: PropTypes.shape(courseOffering),
  registeredCourses: PropTypes.array,
  previouslyLoadedCourse: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    currentOffering: courseSelectors.selectCurrentCourseOffering(state),
    registeredCourses: state.auth.currentUser.registeredCourses,
    previouslyLoadedCourse:
      courseSelectors.selectWasCurrentCoursePreviouslyLoaded(state),
  };
}

export default connect(mapStateToProps)(CourseOfferingDetailBody);
